import React from 'react';
import { FaTimes, FaFlask, FaExclamationTriangle, FaInfoCircle, FaShieldAlt, FaStethoscope, FaClipboardCheck, FaRobot } from 'react-icons/fa';

const InteractionModal = ({ interaction, onClose }) => {
  const isHerbDrug = !!interaction.herb;
  
  const severityConfig = {
    'Major': {
      bg: 'bg-neo-danger',
      light: 'bg-neo-danger', // Used for backgrounds, might need opacity or just solid
      border: 'border-neo-main',
      text: 'text-neo-main',
      badge: 'bg-white text-neo-danger border-2 border-neo-main',
      iconColor: 'text-neo-main'
    },
    'Moderate': {
      bg: 'bg-neo-warning',
      light: 'bg-neo-warning',
      border: 'border-neo-main',
      text: 'text-neo-main',
      badge: 'bg-white text-neo-warning border-2 border-neo-main',
      iconColor: 'text-neo-main'
    },
    'Minor': {
      bg: 'bg-neo-card',
      light: 'bg-neo-card',
      border: 'border-neo-main',
      text: 'text-neo-text',
      badge: 'bg-white text-neo-text border-2 border-neo-main',
      iconColor: 'text-neo-text'
    },
  };

  const config = severityConfig[interaction.severity] || severityConfig['Minor'];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 modal-overlay">
      <div className="bg-neo-card rounded-lg border-2 border-neo-main shadow-neo-brutal max-w-2xl w-full max-h-[90vh] overflow-hidden modal-content flex flex-col">
        {/* Header */}
        <div className={`${config.bg} ${config.text} p-6 border-b-2 border-neo-main relative overflow-hidden flex-shrink-0`}>
          <div className="relative flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white text-neo-main rounded-md flex items-center justify-center border-2 border-neo-main shadow-neo-sm">
                <FaFlask className="text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-black">{isHerbDrug ? 'Herb-Drug Interaction' : 'Drug-Drug Interaction'}</h2>
                <p className="font-bold opacity-90 mt-1">Detailed Analysis & Management</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="w-10 h-10 bg-white text-neo-main hover:bg-neo-bg rounded-md flex items-center justify-center 
                       transition-all duration-200 border-2 border-neo-main shadow-neo-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
            >
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar space-y-6 animate-fade-in">
          <div className={`bg-white p-6 rounded-lg border-2 border-neo-main shadow-neo-sm`}>
            <div className="text-center">
              <p className="text-3xl font-black text-neo-main flex items-center justify-center gap-4 flex-wrap">
                <span className="text-neo-success decoration-2 underline decoration-neo-main">{isHerbDrug ? interaction.herb : interaction.drug_a}</span>
                <span className="text-2xl text-neo-text-sub font-black">+</span>
                <span className="text-neo-accent decoration-2 underline decoration-neo-main">{isHerbDrug ? interaction.drug : interaction.drug_b}</span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className={`bg-white p-5 rounded-lg border-2 border-neo-main shadow-neo-sm`}>
              <div className="flex items-center gap-2 mb-2">
                <FaExclamationTriangle className="text-neo-main" />
                <p className="text-sm font-black text-neo-text-sub uppercase tracking-wide">Severity Level</p>
              </div>
              <p className={`text-2xl font-black ${config.text} bg-neo-bg/20 inline-block px-2 rounded`}>{interaction.severity}</p>
            </div>
            
            {interaction.confidence_score ? (
              <div className="bg-white p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaRobot className="text-neo-accent" />
                  <p className="text-sm font-black text-neo-text-sub uppercase tracking-wide">AI Confidence</p>
                </div>
                <div className="flex items-baseline gap-2">
                    <p className="text-2xl font-black text-neo-accent">
                        {Math.round(interaction.confidence_score * 100)}%
                    </p>
                    {interaction.source && (
                        <span className="text-xs text-neo-text bg-neo-bg px-2 py-0.5 rounded-md border border-neo-main font-bold">
                            Source: {interaction.source}
                        </span>
                    )}
                </div>
              </div>
            ) : (
              <div className="bg-white p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaInfoCircle className="text-neo-accent" />
                  <p className="text-sm font-black text-neo-text-sub uppercase tracking-wide">{interaction.evidence ? 'Evidence Strength' : 'Source'}</p>
                </div>
                <p className="text-2xl font-black text-neo-accent capitalize">{interaction.evidence || interaction.source || 'Established'}</p>
              </div>
            )}
          </div>

          {/* Clinical Interpretation / Effect */}
          <div className="bg-neo-card p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
            <h3 className="text-lg font-black text-neo-main mb-3 flex items-center gap-2">
              <FaShieldAlt className="text-neo-main" />
              {interaction.clinical_interpretation ? 'Clinical Interpretation' : 'Clinical Effect'}
            </h3>
            <p className="text-neo-text font-medium bg-white p-4 rounded-md border-2 border-neo-main leading-relaxed">
              {interaction.clinical_interpretation || interaction.effect || 'No specific clinical details available.'}
            </p>
          </div>

          {/* Mechanism */}
          <div className="bg-neo-bg p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
            <h3 className="text-lg font-black text-neo-main mb-3">Mechanism</h3>
            <p className="text-neo-text font-medium bg-white p-4 rounded-md border-2 border-neo-main leading-relaxed">
              {interaction.mechanism || 'Pharmacodynamic or pharmacokinetic interaction possible'}
            </p>
          </div>

          {/* Management Guidance (Gemini) or Standard Recs */}
          <div className={`${config.bg === 'bg-neo-card' ? 'bg-white' : config.bg} p-5 rounded-lg border-2 border-neo-main shadow-neo-brutal`}>
            <h3 className={`text-lg font-black ${config.text} mb-4 flex items-center gap-2`}>
              <FaStethoscope />
              Management Guidance
            </h3>
            
            {interaction.management_guidance ? (
                 <div className="bg-white p-4 rounded-md border-2 border-neo-main">
                    <p className="text-neo-text font-medium">{interaction.management_guidance}</p>
                 </div>
            ) : (
                <div className="bg-white p-4 rounded-md border-2 border-neo-main">
                {interaction.severity === 'Major' && (
                  <ul className="space-y-3">
                    {[
                      { text: 'AVOID this combination if possible', bold: true },
                      { text: 'Discontinue one agent or consider alternative' },
                      { text: 'If continuation is necessary, implement intensive monitoring' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-neo-danger">
                        <span className="w-6 h-6 bg-neo-danger text-white border border-neo-main rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold">{i + 1}</span>
                        </span>
                        <span className={`text-neo-text ${item.bold ? 'font-black' : 'font-medium'}`}>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                {interaction.severity === 'Moderate' && (
                  <ul className="space-y-3">
                    {[
                      { text: 'Use with CAUTION', bold: true },
                      { text: 'Consider dose adjustment or timing separation' },
                      { text: 'Patient education about warning signs' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-neo-warning">
                        <span className="w-6 h-6 bg-neo-warning text-neo-main border border-neo-main rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold">{i + 1}</span>
                        </span>
                        <span className={`text-neo-text ${item.bold ? 'font-black' : 'font-medium'}`}>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                {interaction.severity === 'Minor' && (
                  <ul className="space-y-3">
                    {[
                      { text: 'Generally safe to continue' },
                      { text: 'Routine monitoring recommended' },
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-neo-text">
                        <span className="w-6 h-6 bg-neo-bg text-neo-main border border-neo-main rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold">{i + 1}</span>
                        </span>
                        <span className="font-medium">{item.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
                </div>
            )}
          </div>

          {/* Monitoring Guidance (Gemini) */}
          {interaction.monitoring_guidance && (
              <div className="bg-neo-bg p-5 rounded-lg border-2 border-neo-main shadow-neo-sm">
                <h3 className="text-lg font-black text-neo-main mb-3 flex items-center gap-2">
                  <FaClipboardCheck className="text-neo-accent" />
                  Monitoring Requirements
                </h3>
                <p className="text-neo-text font-medium bg-white p-4 rounded-md border-2 border-neo-main leading-relaxed">
                  {interaction.monitoring_guidance}
                </p>
              </div>
          )}

          {/* AI Disclaimer for Gemini/Simulated */}
          {(interaction.evidence === 'simulated' || interaction.source === 'Gemini') && (
            <div className="bg-neo-warning border-2 border-neo-main p-5 rounded-lg shadow-neo-sm">
              <div className="flex items-start gap-3">
                <FaInfoCircle className="text-neo-main text-xl flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-black text-neo-main mb-1">AI-Enhanced Analysis</p>
                  <p className="text-sm text-neo-main font-bold opacity-80">
                    This analysis includes AI-generated clinical interpretation. 
                    Clinical evidence may be limited. Use clinical judgment and consider consulting 
                    a pharmacist or clinical decision support specialist.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white p-4 flex justify-end border-t-2 border-neo-main flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-neo-main text-white border-2 border-neo-main rounded-md font-bold 
                     hover:bg-gray-800 transition-all duration-200 shadow-neo-brutal hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default InteractionModal;
